import React, { useContext, useState, useEffect } from 'react';
import moment from 'moment';

import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';

import { handlePageNumberInPagination } from 'common/pagination';
import { toastConstant } from 'constants/toastmessage';
import { useToast } from 'context/ToastContext';
import { UserDetailsContext } from 'context/userDetailsContext';
import {
  generateReport,
  exportReport,
  userGroupReportRds,
  openCaseListRds,
  closedCaseListRds,
  openCaseCardRds,
  closedCaseCardRds,
} from 'services/report/reportServices';
import { convertObjectToQuery, paginationDropdownOptions } from 'utils/utils';
import { convertToTimezoneFormat } from 'utils/utility_functions/timezone';
import { getTimezoneOffset } from 'utils/utility_functions/timezone';

export function useReports({ type, _filter, _pageSize = 10 }) {
  const userContext = useContext(UserDetailsContext);
  const { addToast } = useToast();

  const isCaseReportRds = type === 'case_report';

  const [data, setData] = useState([]);
  const [cardData, setCardData] = useState([]);
  const [isForward, setIsForward] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setpageSize] = useState(userContext?.firmDetails?.pagination_preference?.report?.[type] || _pageSize);
  const next_Token = data?.next_token;
  const transition_Token = data?.transition_token;
  const pageCount = isCaseReportRds ? data?.page_count || 0 : 0;
  const list = data?.reports || [];
  // Handle different totals structure for group_report_rds
  let totals;
  if (type === 'group_report_rds') {
    totals = {
      total_count: data?.total_count || 0,
      billable_hours: data?.total_hours_billed || 0,
      logged_hours: data?.total_hours_logged || 0,
      percentage:
        data?.total_hours_logged && data?.total_hours_logged > 0
          ? Math.round((data.total_hours_billed / data.total_hours_logged) * 100)
          : 0,
    };
  } else {
    if (type === 'case_report') {
      totals = {
        total_billable_hours: cardData?.billable_hours || 0,
        total_case_count: cardData?.count_totals || 0,
        total_logged_hours: cardData?.logged_hours || 0,
      };
    } else {
      totals = data?.conversion_rate || data?.totals || { total_count: data?.total_count } || {};
    }
  }

  useEffect(() => {
    setpageSize(userContext?.firmDetails?.pagination_preference?.report?.[type] || _pageSize);
  }, [userContext]); // eslint-disable-line

  const hasNext = isCaseReportRds ? currentPage >= pageCount : next_Token === null && isForward;
  const hasPrevious = isCaseReportRds
    ? currentPage <= 1
    : transition_Token === null || (transition_Token && next_Token === null && !isForward);

  const paginatorTemplate = {
    layout: 'RowsPerPageDropdown PrevPageLink NextPageLink',
    RowsPerPageDropdown: () => {
      return (
        <React.Fragment>
          <span className="mx-1" style={{ color: 'var(--black-500)', userSelect: 'none' }}>
            Item Per Page
          </span>
          <Dropdown value={pageSize} options={paginationDropdownOptions} onChange={onPageChange} />
        </React.Fragment>
      );
    },
    NextPageLink: ({ iconClassName }) => {
      return (
        <Button onClick={fetchNext} disabled={hasNext} className={'p-paginator-next p-paginator-element p-link'} icon={iconClassName} />
      );
    },
    PrevPageLink: ({ iconClassName }) => {
      return (
        <Button onClick={fetchprev} disabled={hasPrevious} className={'p-paginator-next p-paginator-element p-link'} icon={iconClassName} />
      );
    },
  };

  const fetchNext = () => {
    if (isCaseReportRds) {
      const nextPage = currentPage + 1;
      if (nextPage <= pageCount) {
        setCurrentPage(nextPage);
        loadReportList({
          limit: pageSize,
          page: nextPage,
          filter: _filter,
        });
      }
      return;
    }
    setIsForward(true);
    loadReportList(
      {
        limit: pageSize,
        pagination_token: !isForward ? transition_Token : next_Token,
        pagination_direction: 'forward',
      },
      _filter
    );
  };

  const fetchprev = () => {
    if (isCaseReportRds) {
      const prevPage = currentPage - 1;
      if (prevPage >= 1) {
        setCurrentPage(prevPage);
        loadReportList({
          limit: pageSize,
          page: prevPage,
          filter: _filter,
        });
      }
      return;
    }
    setIsForward(false);
    loadReportList(
      {
        limit: pageSize,
        pagination_token: isForward ? transition_Token : next_Token,
        pagination_direction: 'reverse',
      },
      _filter
    );
  };

  const onPageChange = (e) => {
    setpageSize(e.value);
    handlePageNumberInPagination(userContext, 'report', '', type, e.value);
    if (isCaseReportRds) {
      setCurrentPage(1);
      loadReportList({
        limit: e.value,
        page: 1,
        filter: _filter,
      });
    } else {
      loadReportList(
        {
          limit: e.value,
          pagination_token: '',
          pagination_direction: 'forward',
        },
        _filter
      );
    }
    setIsForward(true);
  };

  const loadReportList = (options = {}) => {
    const {
      limit = pageSize || 10,
      pagination_token = '',
      pagination_direction = 'forward',
      filter = _filter,
      timezone_offset = getTimezoneOffset(userContext?.userDetails?.timezone),
    } = options;

    const requestPage = isCaseReportRds ? (options.page !== undefined ? options.page : 1) : undefined;
    if (isCaseReportRds && options.page === undefined) {
      setCurrentPage(1);
    }

    const qs = {
      pagination_token,
      pagination_direction,
      limit,
      timezone_offset,
    };
    let filterData =
      type === 'trust_account_transaction_report' && !Array.isArray(filter?.account_list)
        ? { ...filter, account_list: [filter.account_list] }
        : filter;
    if (type === 'client_report') {
      const { client_interaction, ...otherFilter } = filter;
      filterData = filter?.client_interaction !== null ? filter : otherFilter;
    }
    if (type === 'payment_report') {
      const { payment_plan, ...otherFilter } = filter;
      filterData = filter?.payment_plan !== null ? filter : otherFilter;
    }
    if (type === 'firm_user_report' || type === 'group_report_rds') {
      const { firm_user_group_list, ...otherFilter } = filter;
      filterData = {
        ...otherFilter,
        user_group_list: [],
        special_firm_list: [],
      };
      if (firm_user_group_list?.length) {
        firm_user_group_list?.forEach((item) => {
          if (item?.res_type === 'user-group') {
            filterData.user_group_list.push(item?.sk);
          } else {
            filterData.special_firm_list.push(item?.firm_user_id);
          }
        });
      }
    }
    const data = {
      ...(!isCaseReportRds && { report_type: type }),
      ...filterData,
      ...(filter?.report_start && {
        report_start: convertToTimezoneFormat(moment(filter.report_start).startOf('day'), userContext?.userDetails.timezone),
      }),
      ...(filter?.report_end && {
        report_end: convertToTimezoneFormat(moment(filter.report_end).endOf('day'), userContext?.userDetails.timezone),
      }),
      ...(isCaseReportRds && { page: requestPage, limit, timezone_offset }),
    };

    // Use custom endpoint for group_report_rds
    let reportPromise = null;
    if (type === 'group_report_rds') {
      reportPromise = userGroupReportRds(convertObjectToQuery(qs), data);
    } else if (type === 'case_report') {
      reportPromise = openCaseListRds(data);
    } else {
      reportPromise = generateReport(convertObjectToQuery(qs), data);
    }

    reportPromise
      .then((response) => {
        // Handle different response structures for different report types
        let reports = response?.data?.reports;

        // For group_report_rds, the data is in the 'data' field, not 'reports'
        if (type === 'group_report_rds') {
          reports = response?.data?.data || [];

          // Map the API response fields to frontend expected fields
          reports = reports?.map((user) => ({
            ...user,
            user_name: `${user.first_name}${user.middle_name ? ' ' + user.middle_name : ''} ${user.last_name}`.trim(),
            logged_hours: user.hours_logged || 0,
            billable_hours: user.hours_billed || 0,
            message_sent: user.message_count || 0,
            marked_as_resolved: user.case_note_resolved_count || 0,
          }));
        }

        if (type === 'case_report') {
          reports = response?.data?.data || [];
          delete response?.data?.data;

          const processedData = {
            ...response?.data,
            reports:
              reports?.map((v) => ({
                ...v,
                leadAttorney: {
                  attorney_name: v?.lead_attorney_full_name,
                  firm_user_id: v?.lead_attorney_firm_user_id,
                },
                case_description: {
                  case_name: v?.case_name,
                  case_id: v?.case_uuid,
                  case_stage: {
                    stage_name: v?.case_stage_name,
                    case_stage_id: v?.case_stage_uuid,
                  },
                  case_practice_area: v?.practice_area_name,
                  _venue_: v?.venue_name,
                  venue: v?.venue_uuid,
                },
                case_details: {
                  tags: v?.tags,
                },
                case_id: v?.case_uuid,
              })) || [],
          };
          setCurrentPage(response?.data?.page ?? requestPage);
          setData(processedData);
        } else {
          const processedData = {
            ...response?.data,
            reports:
              reports?.map((v) => ({
                ...v,
                leadAttorney: v?.attorney_details?.find((a) => a?.is_lead_attorney),
              })) || [],
          };

          setData(processedData);
        }
      })
      .catch((error) => {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.FAILED_REPORT);
      });
  };

  const loadReportCard = async ({ filter }) => {
    let data = { ...filter, timezone_offset: getTimezoneOffset(userContext?.userDetails?.timezone) };

    let reportPromise = null;

    if (type === 'case_report') {
      reportPromise = openCaseCardRds(data);
    }

    try {
      const response = await reportPromise;
      setCardData(response?.data);
    } catch (error) {
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.FAILED_REPORT);
    }
  };

  const reportExport = ({ filter = _filter }) => {
    // Check if there's any data to export
    if (list?.length === 0) {
      addToast(false, toastConstant.toasterType.INFO, 'No Data', 'There is no data available to export.');
      return;
    }

    const qs = {
      report_type: type,
      ...(filter?.report_start && {
        report_start: convertToTimezoneFormat(moment(filter.report_start).startOf('day'), userContext?.userDetails.timezone),
      }),
      ...(filter?.report_end && {
        report_end: convertToTimezoneFormat(moment(filter.report_end).endOf('day'), userContext?.userDetails.timezone),
      }),
    };
    let exportFilterData = { ...filter };
    const data = {
      ...exportFilterData,
    };
    exportReport(convertObjectToQuery(qs), data)
      .then((response) => {
        const link = document.createElement('a');
        link.href = response?.data?.report_fetch_url;
        document.body.appendChild(link);
        link.click();
      })
      .catch(() => addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.EXPORT_REPORT_FAILED));
  };

  return {
    list,
    totals,
    hasNext,
    hasPrevious,
    pageSize,
    paginatorTemplate,
    fetchNext,
    fetchprev,
    onPageChange,
    loadReportList,
    reportExport,
    loadReportCard,
  };
}
